import React, { useEffect, useRef, useState } from 'react';
import {
  Plus, Trash2, Pencil, Save, X, CheckCircle2, Eye, EyeOff, Upload,
  Image as ImageIcon, Sparkles, Loader2, ThumbsUp, ThumbsDown, Download, RefreshCw,
  FileText,
} from 'lucide-react';
import MDEditor from '@uiw/react-md-editor';
import RichHtmlEditor from '../../lib/RichHtmlEditor';
import { insurersApi, uploadsApi } from '../../lib/api';
import { LANGS } from '../../lib/langs';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';

const empty = {
  slug: '', display_name: '', logo_url: '', website: '',
  english_support: 'unknown', english_support_note: '',
  founded_year: '', am_best_rating: '', claim_response_days_avg: '', market_share_pct: '',
  tagline: '', pros: [''], cons: [''], editor_notes: '',
  aliases: [], product_lines: [], sources: [], published: false,
  article_title: '', article_excerpt: '', article_content: '',
  article_cover_image: '', seo_title: '', seo_description: '', seo_keywords: '',
  article_published: false,
};

const SUPPORT_LEVELS = [
  { v: 'excellent', label: 'Excellent (native English support)' },
  { v: 'good',      label: 'Good (English-speaking staff available)' },
  { v: 'limited',   label: 'Limited (mostly Spanish, some English)' },
  { v: 'none',      label: 'None (Spanish only)' },
  { v: 'unknown',   label: 'Unknown' },
];

const PRODUCTS = ['home', 'health', 'auto', 'life'];

export default function AdminInsurers() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [translateMsg, setTranslateMsg] = useState('');
  const [seedMsg, setSeedMsg] = useState('');
  const [fetchingLogos, setFetchingLogos] = useState(false);
  const [logoMsg, setLogoMsg] = useState('');
  const [lang, setLang] = useState('en');
  const logoInput = useRef(null);
  const articleCoverInput = useRef(null);
  const articleInlineInput = useRef(null);

  const refresh = () => { insurersApi.listAdmin().then(setItems).catch(() => setItems([])); };
  useEffect(refresh, []);

  const startNew = () => { setEditing({ ...empty }); setLang('en'); };
  const edit = (i) => { setEditing({ ...i, pros: i.pros?.length ? i.pros : [''], cons: i.cons?.length ? i.cons : [''] }); setLang('en'); };

  const seed = async () => {
    if (!window.confirm('Seed the 18-company starter catalogue? Existing insurers are preserved. Every new profile is created as unpublished.')) return;
    setSeedMsg('Seeding…');
    try {
      const r = await insurersApi.seed();
      setSeedMsg(`Seeded ${r.inserted} new · ${r.skipped} already existed`);
      refresh();
      setTimeout(() => setSeedMsg(''), 5000);
    } catch (err) { setSeedMsg(err?.response?.data?.detail || 'Seed failed'); }
  };

  const fetchLogos = async (overwrite) => {
    const msg = overwrite
      ? 'Re-fetch and OVERWRITE every insurer logo? Any custom logos you uploaded will be replaced.'
      : 'Auto-fetch a logo for every insurer that does not have one yet? Uses Google\'s favicon service.';
    if (!window.confirm(msg)) return;
    setFetchingLogos(true); setLogoMsg('Fetching logos…');
    try {
      const r = await insurersApi.fetchLogos(!!overwrite);
      const parts = [`${r.fetched} fetched`];
      if (r.skipped) parts.push(`${r.skipped} skipped (already had a logo)`);
      if (r.failed?.length) parts.push(`${r.failed.length} failed`);
      setLogoMsg(parts.join(' · '));
      refresh();
      setTimeout(() => setLogoMsg(''), 8000);
    } catch (err) {
      setLogoMsg(err?.response?.data?.detail || 'Fetch failed');
    } finally { setFetchingLogos(false); }
  };

  const translateAll = async () => {
    if (!window.confirm('Auto-translate every insurer profile (tagline, pros, cons, notes) into all 15 other languages? Existing translations are preserved. May take up to 2 minutes.')) return;
    setTranslating(true); setTranslateMsg('Translating…');
    try {
      // Per-lang loop to avoid ingress timeout
      const targets = LANGS.filter((l) => l.code !== 'en').map((l) => l.code);
      let total = 0;
      for (let i = 0; i < targets.length; i++) {
        const code = targets[i];
        setTranslateMsg(`Translating ${code.toUpperCase()} (${i + 1}/${targets.length})…`);
        try {
          const r = await insurersApi.translateAll([code], true);
          total += r.translated_fields || 0;
        } catch (err) { console.warn(`Insurer translate ${code} failed`, err); }
      }
      setTranslateMsg(`Done — translated ${total} fields across ${targets.length} languages.`);
      refresh();
      setTimeout(() => setTranslateMsg(''), 7000);
    } catch (err) {
      setTranslateMsg(err?.response?.data?.detail || 'Auto-translate failed');
    } finally {
      setTranslating(false);
    }
  };

  const save = async () => {
    const clean = {
      ...editing,
      pros: (editing.pros || []).map((p) => (p || '').trim()).filter(Boolean),
      cons: (editing.cons || []).map((p) => (p || '').trim()).filter(Boolean),
      founded_year: editing.founded_year ? parseInt(editing.founded_year, 10) : null,
      claim_response_days_avg: editing.claim_response_days_avg ? parseFloat(editing.claim_response_days_avg) : null,
      market_share_pct: editing.market_share_pct ? parseFloat(editing.market_share_pct) : null,
    };
    try {
      const saved = editing.id
        ? await insurersApi.update(editing.id, clean)
        : await insurersApi.create(clean);
      setEditing(saved);
      setSaved(true); setTimeout(() => setSaved(false), 2500);
      refresh();
    } catch (err) {
      alert('Save failed: ' + (err?.response?.data?.detail || err.message));
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this insurer profile?')) return;
    await insurersApi.remove(id);
    refresh();
    if (editing?.id === id) setEditing(null);
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadsApi.uploadImage(file);
      setEditing((e) => ({ ...e, logo_url: uploadsApi.publicUrl(res.url) }));
    } catch (err) { alert('Upload failed: ' + (err?.response?.data?.detail || err.message)); }
    finally { setUploading(false); if (logoInput.current) logoInput.current.value = ''; }
  };

  const uploadArticleCover = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadsApi.uploadImage(file);
      setEditing((e) => ({ ...e, article_cover_image: uploadsApi.publicUrl(res.url) }));
    } catch (err) { alert('Upload failed: ' + (err?.response?.data?.detail || err.message)); }
    finally { setUploading(false); if (articleCoverInput.current) articleCoverInput.current.value = ''; }
  };

  const uploadArticleInline = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadsApi.uploadImage(file);
      const url = uploadsApi.publicUrl(res.url);
      const md = `\n\n![${file.name}](${url})\n\n`;
      setEditing((e) => {
        if (lang === 'en') {
          return { ...e, article_content: (e.article_content || '') + md };
        }
        const buf = e._langBuf || activeLangView();
        return { ...e, _langBuf: { ...buf, article_content: (buf.article_content || '') + md } };
      });
    } catch (err) { alert('Upload failed: ' + (err?.response?.data?.detail || err.message)); }
    finally { setUploading(false); if (articleInlineInput.current) articleInlineInput.current.value = ''; }
  };

  const updateListItem = (key, i, v) =>
    setEditing((e) => ({ ...e, [key]: e[key].map((x, idx) => (idx === i ? v : x)) }));
  const addListItem = (key) => setEditing((e) => ({ ...e, [key]: [...(e[key] || []), ''] }));
  const removeListItem = (key, i) => setEditing((e) => ({ ...e, [key]: e[key].filter((_, idx) => idx !== i) }));

  // per-lang view: swap the translatable fields with the lang overrides
  const activeLangView = () => {
    if (!editing || lang === 'en') return editing;
    const tr = (editing.translations || {})[lang] || {};
    return {
      ...editing,
      tagline: tr.tagline ?? '',
      english_support_note: tr.english_support_note ?? '',
      editor_notes: tr.editor_notes ?? '',
      pros: tr.pros?.length ? tr.pros : [''],
      cons: tr.cons?.length ? tr.cons : [''],
      article_title: tr.article_title ?? '',
      article_excerpt: tr.article_excerpt ?? '',
      article_content: tr.article_content ?? '',
      seo_title: tr.seo_title ?? '',
      seo_description: tr.seo_description ?? '',
      seo_keywords: tr.seo_keywords ?? '',
    };
  };
  const saveLang = async () => {
    if (!editing?.id) { alert('Save the English draft first.'); return; }
    const buf = editing._langBuf || {};
    const payload = {
      tagline: buf.tagline || '',
      english_support_note: buf.english_support_note || '',
      editor_notes: buf.editor_notes || '',
      pros: (buf.pros || []).filter(Boolean),
      cons: (buf.cons || []).filter(Boolean),
      article_title: buf.article_title || '',
      article_excerpt: buf.article_excerpt || '',
      article_content: buf.article_content || '',
      seo_title: buf.seo_title || '',
      seo_description: buf.seo_description || '',
      seo_keywords: buf.seo_keywords || '',
    };
    await fetch(`/api/admin/insurers/${editing.id}/translations/${lang}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + localStorage.getItem('esi_token') },
      body: JSON.stringify(payload),
    });
    setSaved(true); setTimeout(() => setSaved(false), 2500);
    refresh();
  };

  // ------- LIST view -------
  if (!editing) {
    return (
      <div>
        <div className="mb-5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-xl font-bold text-[#0b1f3a]">Insurers</h2>
            <p className="text-slate-500 text-sm mt-1">
              Profiles displayed under each price on the wizard quote result page. Pros, cons and English-support level per insurer.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {saved && <span className="inline-flex items-center gap-1.5 text-sm text-emerald-700"><CheckCircle2 size={16} /> Saved</span>}
            <button onClick={seed} className="btn-outline" data-testid="admin-insurers-seed"><Download size={14} /> Seed 18 companies</button>
            <button onClick={() => fetchLogos(false)} disabled={fetchingLogos} className="btn-outline disabled:opacity-60" data-testid="admin-insurers-fetch-logos" title="Auto-fetch logos for insurers that don't have one yet">
              {fetchingLogos ? <Loader2 size={14} className="animate-spin" /> : <ImageIcon size={14} />}
              {fetchingLogos ? 'Fetching…' : 'Fetch logos'}
            </button>
            <button onClick={translateAll} disabled={translating} className="btn-primary disabled:opacity-60" data-testid="admin-insurers-translate-all">
              {translating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              {translating ? 'Translating…' : 'Translate to 16 languages'}
            </button>
            <button onClick={startNew} className="btn-primary" data-testid="admin-insurers-new"><Plus size={16} /> New insurer</button>
          </div>
        </div>
        {seedMsg && <div className="mb-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg px-3 py-2 text-sm">{seedMsg}</div>}
        {logoMsg && <div className="mb-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg px-3 py-2 text-sm">{logoMsg}</div>}
        {translateMsg && <div className="mb-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg px-3 py-2 text-sm">{translateMsg}</div>}

        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500 tracking-wider">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Insurer</th>
                <th className="text-left px-4 py-2 font-semibold">Lines</th>
                <th className="text-left px-4 py-2 font-semibold">English support</th>
                <th className="text-center px-4 py-2 font-semibold">Pros / Cons</th>
                <th className="text-center px-4 py-2 font-semibold">Published</th>
                <th className="text-right px-4 py-2 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((i) => (
                <tr key={i.id} className="hover:bg-slate-50/50">
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      {i.logo_url ? (
                        <img src={i.logo_url} alt="" className="w-8 h-8 object-contain rounded border border-slate-200 bg-white" />
                      ) : (
                        <span className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center text-slate-400"><ImageIcon size={14} /></span>
                      )}
                      <div>
                        <p className="font-semibold text-[#0b1f3a]">{i.display_name}</p>
                        <p className="text-xs text-slate-500">/{i.slug}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2 text-slate-600">{(i.product_lines || []).join(', ') || '—'}</td>
                  <td className="px-4 py-2"><SupportBadge level={i.english_support} /></td>
                  <td className="px-4 py-2 text-center text-xs">
                    <span className="text-emerald-700 mr-2">✓ {i.pros?.length || 0}</span>
                    <span className="text-rose-700">✗ {i.cons?.length || 0}</span>
                  </td>
                  <td className="px-4 py-2 text-center">
                    {i.published
                      ? <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold"><Eye size={12} /> Live</span>
                      : <span className="inline-flex items-center gap-1 text-slate-500 text-xs"><EyeOff size={12} /> Draft</span>}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button onClick={() => edit(i)} className="text-[#0d3a8a] hover:underline text-sm"><Pencil size={14} /></button>
                      <button onClick={() => remove(i.id)} className="text-rose-600 hover:underline text-sm"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan="6" className="text-center py-14 text-slate-500">
                  No insurers yet. Click <b>Seed 18 companies</b> to import the starter catalogue.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ------- EDIT view -------
  const view = lang === 'en' ? editing : (editing._langBuf || activeLangView());
  const setView = (patch) => {
    if (lang === 'en') { setEditing((e) => ({ ...e, ...patch })); return; }
    setEditing((e) => ({ ...e, _langBuf: { ...(e._langBuf || activeLangView()), ...patch } }));
  };

  return (
    <div className="pb-16">
      <div className="mb-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-[#0b1f3a]">{editing.id ? 'Edit insurer' : 'New insurer'}</h2>
          <p className="text-slate-500 text-sm mt-1">
            English is the source of truth. Non-English tabs override specific fields; leave any override blank to fall back to English.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {saved && <span className="inline-flex items-center gap-1.5 text-sm text-emerald-700"><CheckCircle2 size={16} /> Saved</span>}
          <button onClick={() => setEditing(null)} className="btn-outline"><X size={14} /> Close</button>
          {lang === 'en'
            ? <button onClick={save} className="btn-primary"><Save size={14} /> Save</button>
            : <button onClick={saveLang} className="btn-primary"><Save size={14} /> Save {lang.toUpperCase()}</button>}
        </div>
      </div>

      {/* Language tabs */}
      <div className="mb-5 bg-white border border-slate-200 rounded-2xl p-3">
        <div className="flex flex-wrap gap-1.5" data-testid="admin-insurer-lang-tabs">
          {LANGS.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => setLang(l.code)}
              className={`h-8 min-w-[56px] px-2.5 rounded-lg text-xs font-semibold border transition-colors ${
                lang === l.code
                  ? 'bg-[#0d3a8a] text-white border-[#0d3a8a]'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
            >
              {l.flag} {l.code.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-6">
        {/* Basics — English only */}
        {lang === 'en' && (
          <section className="bg-white border border-slate-200 rounded-2xl p-6">
            <h3 className="font-semibold text-[#0b1f3a] mb-4">Basics</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Display name"><Input value={editing.display_name} onChange={(e) => setEditing({ ...editing, display_name: e.target.value })} /></Field>
              <Field label="Slug (URL)"><Input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} placeholder="mapfre" /></Field>
              <Field label="Website"><Input value={editing.website || ''} onChange={(e) => setEditing({ ...editing, website: e.target.value })} placeholder="https://…" /></Field>
              <Field label="AM Best rating"><Input value={editing.am_best_rating || ''} onChange={(e) => setEditing({ ...editing, am_best_rating: e.target.value })} placeholder="A+" /></Field>
              <Field label="Founded year"><Input type="number" value={editing.founded_year || ''} onChange={(e) => setEditing({ ...editing, founded_year: e.target.value })} /></Field>
              <Field label="Market share (%)"><Input type="number" step="0.1" value={editing.market_share_pct || ''} onChange={(e) => setEditing({ ...editing, market_share_pct: e.target.value })} /></Field>
              <Field label="Avg claim response (days)"><Input type="number" step="0.1" value={editing.claim_response_days_avg || ''} onChange={(e) => setEditing({ ...editing, claim_response_days_avg: e.target.value })} /></Field>
              <Field label="Aliases (comma-separated)">
                <Input value={(editing.aliases || []).join(', ')} onChange={(e) => setEditing({ ...editing, aliases: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} placeholder="MAPFRE, Mapfre España" />
              </Field>
            </div>

            <div className="mt-5">
              <p className="field-label mb-2">Logo</p>
              <div className="flex items-start gap-4">
                {editing.logo_url ? (
                  <div className="relative w-24 h-24 rounded-xl overflow-hidden border border-slate-200 bg-white p-2 shrink-0">
                    <img src={editing.logo_url} alt="" className="w-full h-full object-contain" />
                  </div>
                ) : (
                  <div className="w-24 h-24 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center text-slate-400 shrink-0"><ImageIcon size={22} /></div>
                )}
                <div className="flex-1 space-y-2">
                  <Input value={editing.logo_url || ''} onChange={(e) => setEditing({ ...editing, logo_url: e.target.value })} placeholder="Paste a URL or upload below" />
                  <button type="button" onClick={() => logoInput.current?.click()} disabled={uploading} className="btn-outline !py-2 !px-3 text-xs">
                    <Upload size={12} /> {uploading ? 'Uploading…' : 'Upload logo'}
                  </button>
                  <input ref={logoInput} type="file" accept="image/*" hidden onChange={(e) => uploadLogo(e.target.files?.[0])} />
                </div>
              </div>
            </div>

            <div className="mt-5">
              <p className="field-label mb-2">Product lines</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {PRODUCTS.map((p) => (
                  <label key={p} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 cursor-pointer">
                    <span className="text-sm capitalize">{p}</span>
                    <Switch
                      checked={(editing.product_lines || []).includes(p)}
                      onCheckedChange={(v) => {
                        const set = new Set(editing.product_lines || []);
                        v ? set.add(p) : set.delete(p);
                        setEditing({ ...editing, product_lines: Array.from(set) });
                      }}
                    />
                  </label>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* English support */}
        <section className="bg-white border border-slate-200 rounded-2xl p-6">
          <h3 className="font-semibold text-[#0b1f3a] mb-4">English support {lang !== 'en' && <span className="text-xs text-slate-500 font-normal">({lang.toUpperCase()} note only)</span>}</h3>
          {lang === 'en' && (
            <div className="mb-4">
              <Label className="field-label">Support level</Label>
              <Select value={editing.english_support} onValueChange={(v) => setEditing({ ...editing, english_support: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{SUPPORT_LEVELS.map((l) => <SelectItem key={l.v} value={l.v}>{l.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="field-label">Support note (visible to customers)</Label>
            <Textarea rows={3} value={view.english_support_note || ''} onChange={(e) => setView({ english_support_note: e.target.value })} />
          </div>
        </section>

        {/* Tagline */}
        <section className="bg-white border border-slate-200 rounded-2xl p-6">
          <h3 className="font-semibold text-[#0b1f3a] mb-4">Tagline & long-form notes</h3>
          <Field label="One-line tagline (bold under insurer name)">
            <Input value={view.tagline || ''} onChange={(e) => setView({ tagline: e.target.value })} placeholder="The safe default for expats" />
          </Field>
          <div className="mt-4">
            <Label className="field-label">Editor notes (Markdown — shown on the &quot;Read full review&quot; modal)</Label>
            <Textarea rows={6} value={view.editor_notes || ''} onChange={(e) => setView({ editor_notes: e.target.value })} />
          </div>
        </section>

        {/* Pros / Cons */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <PCEditor
            title="Pros"
            icon={ThumbsUp}
            tone="emerald"
            items={view.pros || ['']}
            onChange={(i, v) => setView({ pros: (view.pros || ['']).map((x, idx) => idx === i ? v : x) })}
            onAdd={() => setView({ pros: [...(view.pros || []), ''] })}
            onRemove={(i) => setView({ pros: (view.pros || []).filter((_, idx) => idx !== i) })}
          />
          <PCEditor
            title="Cons"
            icon={ThumbsDown}
            tone="rose"
            items={view.cons || ['']}
            onChange={(i, v) => setView({ cons: (view.cons || ['']).map((x, idx) => idx === i ? v : x) })}
            onAdd={() => setView({ cons: [...(view.cons || []), ''] })}
            onRemove={(i) => setView({ cons: (view.cons || []).filter((_, idx) => idx !== i) })}
          />
        </section>

        {/* ----- Long-form Article (blog-style) ----- */}
        <section className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="admin-insurer-article-section">
          <div className="flex items-center gap-2 mb-1">
            <FileText size={18} className="text-[#0d3a8a]" />
            <h3 className="font-semibold text-[#0b1f3a]">
              Long-form article
              {lang !== 'en' && <span className="ml-2 text-xs text-slate-500 font-normal">({lang.toUpperCase()} translation)</span>}
            </h3>
          </div>
          <p className="text-sm text-slate-500 mb-5">
            A full markdown article about this insurer, shown on the public site under the Blog category
            <b> Insurers</b>. Featured image on top, SEO fields on the bottom. Publishes independently of the
            profile flag above.
          </p>

          {/* Featured image — English only (translations use the same image) */}
          {lang === 'en' && (
            <div className="mb-5">
              <Label className="field-label">Featured image (top of the article)</Label>
              <div className="flex items-start gap-4">
                {editing.article_cover_image ? (
                  <div className="relative w-40 h-28 rounded-xl overflow-hidden border border-slate-200 bg-slate-50 shrink-0">
                    <img src={editing.article_cover_image} alt="cover" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => setEditing({ ...editing, article_cover_image: '' })}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 text-slate-700 hover:bg-white inline-flex items-center justify-center shadow"
                      title="Remove"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <div className="w-40 h-28 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center text-slate-400 shrink-0">
                    <ImageIcon size={22} />
                  </div>
                )}
                <div className="flex-1 space-y-2">
                  <Input
                    value={editing.article_cover_image || ''}
                    onChange={(e) => setEditing({ ...editing, article_cover_image: e.target.value })}
                    placeholder="Paste an image URL or upload"
                    data-testid="admin-insurer-article-cover-url"
                  />
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => articleCoverInput.current?.click()}
                      disabled={uploading}
                      className="btn-outline !py-2 !px-3 text-xs disabled:opacity-60"
                    >
                      <Upload size={14} /> {uploading ? 'Uploading…' : 'Upload featured image'}
                    </button>
                    <p className="text-xs text-slate-500">Auto-compressed to WebP under 200 KB</p>
                  </div>
                  <input ref={articleCoverInput} type="file" accept="image/*" hidden onChange={(e) => uploadArticleCover(e.target.files?.[0])} />
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4">
            <Field label="Article title">
              <Input
                value={view.article_title || ''}
                onChange={(e) => setView({ article_title: e.target.value })}
                placeholder="Falls back to the insurer's display name"
                data-testid="admin-insurer-article-title"
              />
            </Field>
            <Field label="Excerpt (shown on the blog listing card)">
              <Textarea
                rows={2}
                value={view.article_excerpt || ''}
                onChange={(e) => setView({ article_excerpt: e.target.value })}
                data-testid="admin-insurer-article-excerpt"
              />
            </Field>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="field-label !mb-0">Content</Label>
                <p className="text-xs text-slate-500">Paste from Word, Google Docs or LibreOffice - formatting is preserved</p>
              </div>
              <RichHtmlEditor
                value={view.article_content || ''}
                onChange={(html) => setView({ article_content: html })}
                placeholder={'Write the article here...\n\nUse the H1 / H2 / H3 buttons for headings, or paste content straight from Word / Google Docs / LibreOffice - it will be cleaned up automatically.'}
                minHeight={520}
                testid="admin-insurer-article-content"
              />
              <p className="text-xs text-slate-500 mt-1">
                Same editor as News/Blog - headings, bold, links, images, tables. Article appears at
                {' '}<code className="text-[10px] bg-slate-100 px-1 py-0.5 rounded">/insurers/{editing.slug || 'slug'}</code>
                {' '}when the toggle at the bottom is on.
              </p>
            </div>
          </div>

          {/* SEO block */}
          <div className="mt-6 pt-6 border-t border-slate-200">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">SEO</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="SEO title (browser tab / Google result)">
                <Input
                  value={view.seo_title || ''}
                  onChange={(e) => setView({ seo_title: e.target.value })}
                  placeholder={editing.display_name ? `${editing.display_name} Home Insurance Review 2026` : ''}
                  data-testid="admin-insurer-seo-title"
                />
              </Field>
              <Field label="SEO keywords (comma-separated)">
                <Input
                  value={view.seo_keywords || ''}
                  onChange={(e) => setView({ seo_keywords: e.target.value })}
                  placeholder="mapfre review, mapfre home insurance spain, mapfre expats"
                  data-testid="admin-insurer-seo-keywords"
                />
              </Field>
              <div className="md:col-span-2">
                <Label className="field-label">SEO description (shown under the Google result)</Label>
                <Textarea
                  rows={2}
                  value={view.seo_description || ''}
                  onChange={(e) => setView({ seo_description: e.target.value })}
                  placeholder="A concise 150–160 character summary of the article."
                  data-testid="admin-insurer-seo-description"
                />
              </div>
            </div>
          </div>

          {/* Publish article — English only */}
          {lang === 'en' && (
            <div className="mt-6 pt-6 border-t border-slate-200 flex items-center justify-between">
              <div>
                <p className="font-semibold text-[#0b1f3a]">Publish article</p>
                <p className="text-sm text-slate-500 mt-0.5">
                  When on, the article appears under the Blog category <b>Insurers</b> and at
                  {' '}<code className="text-[10px] bg-slate-100 px-1 py-0.5 rounded">/insurers/{editing.slug || 'slug'}</code>.
                </p>
              </div>
              <Switch
                checked={!!editing.article_published}
                onCheckedChange={(v) => setEditing({ ...editing, article_published: v })}
                data-testid="admin-insurer-article-published"
              />
            </div>
          )}
        </section>

        {/* Publish flag — English only */}
        {lang === 'en' && (
          <section className="bg-white border border-slate-200 rounded-2xl p-6 flex items-center justify-between">
            <div>
              <p className="font-semibold text-[#0b1f3a]">Publish this profile</p>
              <p className="text-sm text-slate-500 mt-0.5">Draft profiles never appear on the public site, even when the wizard returns a quote for them.</p>
            </div>
            <Switch checked={editing.published} onCheckedChange={(v) => setEditing({ ...editing, published: v })} />
          </section>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div><Label className="field-label">{label}</Label>{children}</div>;
}

function SupportBadge({ level }) {
  const map = {
    excellent: ['bg-emerald-100 text-emerald-800', '🇬🇧 Excellent'],
    good:      ['bg-emerald-50 text-emerald-700', '🇬🇧 Good'],
    limited:   ['bg-amber-50 text-amber-700', '🇬🇧 Limited'],
    none:      ['bg-rose-50 text-rose-700', '🇬🇧 None'],
    unknown:   ['bg-slate-100 text-slate-600', '🇬🇧 Unknown'],
  };
  const [cls, label] = map[level] || map.unknown;
  return <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
}

function PCEditor({ title, icon: Icon, tone, items, onChange, onAdd, onRemove }) {
  const border = tone === 'emerald' ? 'border-emerald-200' : 'border-rose-200';
  const text = tone === 'emerald' ? 'text-emerald-700' : 'text-rose-700';
  return (
    <div className={`bg-white border ${border} rounded-2xl p-5`}>
      <div className={`flex items-center gap-2 mb-3 font-semibold ${text}`}>
        <Icon size={16} /> {title}
      </div>
      <div className="space-y-2">
        {items.map((v, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input value={v} onChange={(e) => onChange(i, e.target.value)} placeholder={`${title} #${i + 1}`} />
            <button type="button" onClick={() => onRemove(i)} className="text-slate-400 hover:text-rose-600"><X size={14} /></button>
          </div>
        ))}
      </div>
      <button type="button" onClick={onAdd} className="btn-outline mt-3 !py-1.5 !px-3 text-xs"><Plus size={12} /> Add {title.toLowerCase().slice(0, -1)}</button>
    </div>
  );
}
